'use client';

import { usePathname } from 'next/navigation';
import { RefObject, useEffect } from 'react';

import { sendGTMEventEcommerce } from '../lib/events';
import { GTMEcommerceItem } from '../lib/types';

interface GTMViewItemListProps {
  refItem: RefObject<HTMLElement | null>;
  item: GTMEcommerceItem;
}

const maxItems = 8;
const timeoutPush = 100;
let t = -1;
let items: GTMEcommerceItem[] = [];

const debouncePushItem = (item: GTMEcommerceItem) => {
  window.clearTimeout(t);
  items.push(item);
  if (items.length >= maxItems) {
    pushItems();
    return;
  }
  t = window.setTimeout(() => {
    pushItems();
  }, timeoutPush);
};

const pushItems = () => {
  if (items.length === 0) return;
  window.clearTimeout(t);
  sendGTMEventEcommerce({
    event: 'impressions',
    ecommerce: {
      products: items
    }
  });
  items = [];
};

export const GTMViewItemList = ({ refItem, item }: GTMViewItemListProps) => {
  const pathname = usePathname();

  useEffect(() => {
    return () => pushItems();
  }, [pathname]);

  useEffect(() => {
    const itemElement = refItem.current;
    if (!itemElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.unobserve(itemElement);
          debouncePushItem(item);
        }
      },
      { threshold: 0.5, rootMargin: '0px 0px 0px 0px' }
    );

    observer.observe(itemElement);

    return () => observer.disconnect();
  }, [refItem, pathname]);

  return null;
};
